import React from 'react'
import { StyleSheet, View, ScrollView } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { Disponibility, getDayLabel, getMomentLabel } from '@utils'
import { getComponentsStyles } from './styles'
import { Moment } from '@hooks'

export type AvailabilitySectionProps = {
  title: string
  disponibilities: Disponibility[]
  bestMoments: Moment[]
  noEdit?: boolean
  onEdit?: () => void
}

export const AvailabilitySection = ({
  title,
  disponibilities,
  bestMoments,
  noEdit = false,
  onEdit,
}: AvailabilitySectionProps): React.ReactElement => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyle(theme)
  const componentsStyles = getComponentsStyles(theme)
  
  return (
    <View style={styles.container}>
      <View style={styles.topContainer}>
        <Text type="bodyBold" color={theme.colors.primary} style={styles.title}>
          {title}
        </Text>
        {!noEdit && <Icon
          name="edit"
          color={theme.colors.greyBlue}
          size={theme.iconSize.medium}
          containerStyle={styles.editIcon}
          onPress={onEdit}
        />}
      </View>

      <View style={styles.sectionContainer}>
        {disponibilities.length > 0 && (
          <View style={styles.availabilityHeader}>
            <Text type="bodyBold" color={theme.colors.secondary}>
              {t('form.availability')}
            </Text>
            <Icon 
              name="calendar" 
              color={theme.colors.primary} 
              size={theme.iconSize.large}
            />
          </View>)
        }
        {disponibilities.length === 0 && (
          <View style={componentsStyles.emptyState}>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={componentsStyles.emptyText}>
              {t('profileScreen.noAvailabilityAdded')}
            </Text>
          </View>
        )}
        
        {disponibilities.map((disponibility, index) => (
          <View key={index} style={styles.availabilityItem}>
            <Text type="body" color={theme.colors.secondary}>
              {getDayLabel(t, disponibility.dayStart.id)} - {getDayLabel(t, disponibility.dayEnd.id)} {disponibility.timeStart} - {disponibility.timeEnd}
            </Text>
          </View>
        ))}

        {/* Section Meilleur moment */}
        {bestMoments.length > 0 && (
          <View style={styles.bestMomentsSection}>
            <Text type="bodyBold" color={theme.colors.secondary} style={styles.bestMomentsTitle}>
              {t('form.bestMoment')}
            </Text>
            
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.bestMomentsScrollContent}
              style={styles.bestMomentsScroll}
            >
              {bestMoments.map((moment, index) => (
                <View key={moment.id || index} style={styles.bestMomentItem}>
                  <Icon 
                    name={moment.code} 
                    color={theme.colors.primary} 
                    size={theme.iconSize.large}
                  />
                  <Text type="bodySmall" color={theme.colors.primary} style={styles.bestMomentLabel}>
                    {getMomentLabel(t, moment.code, moment.nameFr, moment.nameEn, currentLanguage, moment.nameEs)}
                  </Text>
                </View>
              ))}
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      marginHorizontal: theme.spacing.xl,
    },
    topContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
    },
    title: {
    },
    editIcon: {
      padding: theme.spacing.sm,
    },
    sectionContainer: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.medium,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.lg,
    },
    availabilityHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.md,
    },
    availabilityItem: {
      marginBottom: theme.spacing.sm,
    },
    bestMomentsSection: {
      marginTop: theme.spacing.lg,
      paddingTop: theme.spacing.lg,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
    },
    bestMomentsTitle: {
      marginBottom: theme.spacing.lg,
    },
    bestMomentsScroll: {
      flexGrow: 0,
    },
    bestMomentsScrollContent: {
      paddingRight: theme.spacing.lg,
    },
    bestMomentItem: {
      alignItems: 'center',
      marginRight: theme.spacing.xl,
      minWidth: 80,
    },
    bestMomentLabel: {
      marginTop: theme.spacing.sm,
      textAlign: 'center',
    },
  }) 